import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  IconButton,
  ImageList,
  ImageListItem,
  CircularProgress,
  FormControlLabel,
  Switch,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Delete,
  Phone,
  Email,
  Save,
} from '@mui/icons-material';
import api from '../../services/api';

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface ListingData {
  title: string;
  description: string;
  price: number;
  category_id: number | null;
  location: string;
  contact_phone: string;
  contact_email: string;
  prefer_phone: boolean;
  status: string;
  images: string[];
}

const EditListingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ListingData>({
    title: '',
    description: '',
    price: 0,
    category_id: null,
    location: '',
    contact_phone: '',
    contact_email: '',
    prefer_phone: true,
    status: 'active',
    images: [],
  });

  useEffect(() => {
    fetchListingData();
    fetchCategories();
  }, [id]);

  const fetchListingData = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/marketplace/listings/${id}`);
      const listing = response.data;
      
      setFormData({
        title: listing.title,
        description: listing.description,
        price: listing.price,
        category_id: listing.category_id,
        location: listing.location || '',
        contact_phone: listing.contact_phone || '',
        contact_email: listing.contact_email || '',
        prefer_phone: listing.prefer_phone ?? true,
        status: listing.status,
        images: listing.images || [],
      });
      
      setExistingImages(listing.images || []);
    } catch (error: any) {
      console.error('Error fetching listing:', error);
      setError(error.response?.data?.detail || t('marketplace.edit.fetchError'));
      
      if (error.response?.status === 404) {
        navigate('/app/marketplace/my-listings');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      category_id: event.target.value || null,
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    const totalImages = existingImages.length - imagesToDelete.length + newImages.length + files.length;
    if (totalImages > 5) {
      setError(t('marketplace.edit.maxImagesError'));
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name} ${t('marketplace.edit.fileSizeError')}`);
        return false;
      }
      return true;
    });

    setNewImages(prev => [...prev, ...validFiles]);
    
    // Create preview URLs
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...newPreviews]);
    setError(null);
  };

  const handleRemoveNewImage = (index: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageUrl: string) => {
    setImagesToDelete(prev => [...prev, imageUrl]);
  };

  const handleRestoreImage = (imageUrl: string) => {
    setImagesToDelete(prev => prev.filter(url => url !== imageUrl));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    if (!formData.title || !formData.description || !formData.price) {
      setError(t('marketplace.edit.requiredFieldsError'));
      return;
    }
    
    if (!formData.contact_email && !formData.contact_phone) {
      setError(t('marketplace.edit.contactMethodError'));
      return;
    }

    setSaving(true);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      
      // Add listing data
      submitData.append('listing_data', JSON.stringify({
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price.toString()),
        category_id: formData.category_id,
        location: formData.location,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        prefer_phone: formData.prefer_phone,
        images_to_delete: imagesToDelete,
      }));

      // Append new images
      newImages.forEach(image => {
        submitData.append('files', image);
      });

      await api.put(`/marketplace/listings/${id}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Success - navigate back to my listings
      navigate('/app/marketplace/my-listings');
    } catch (error: any) {
      console.error('Error updating listing:', error);
      setError(error.response?.data?.detail || t('marketplace.edit.updateError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={100} />
        </Paper>
      </Container>
    );
  }

  const remainingImages = existingImages.filter(img => !imagesToDelete.includes(img));
  const totalImageCount = remainingImages.length + newImages.length;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/app/marketplace/my-listings')}
        sx={{ mb: 2 }}
      >
        {t('marketplace.edit.backToMyListings')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('marketplace.edit.title')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            required
            label={t('marketplace.create.listingTitle')}
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
          />

          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label={t('marketplace.create.description')}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
          />

          <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
            <TextField
              required
              label={t('marketplace.create.price')}
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              sx={{ flex: 1 }}
            />

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>{t('marketplace.search.category')}</InputLabel>
              <Select
                value={formData.category_id || ''}
                onChange={handleCategoryChange}
                label={t('marketplace.search.category')}
              >
                <MenuItem value="">
                  <em>{t('marketplace.create.noCategory')}</em>
                </MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.slug ? t(`marketplace.categories.${category.slug}`, { defaultValue: category.name }) : category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('marketplace.create.location')}
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            {t('marketplace.create.contactInfo')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label={t('marketplace.create.phone')}
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />

            <TextField
              label={t('marketplace.create.email')}
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.prefer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, prefer_phone: e.target.checked }))}
              />
            }
            label={t('marketplace.create.preferPhone')}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            {t('marketplace.create.images')} ({totalImageCount}/5)
          </Typography>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('marketplace.edit.existingImages')}
              </Typography>
              <ImageList sx={{ height: 200 }} cols={5} rowHeight={164}>
                {existingImages.map((imageUrl, index) => {
                  const isDeleted = imagesToDelete.includes(imageUrl);
                  return (
                    <ImageListItem key={`existing-${index}`} sx={{ position: 'relative' }}>
                      <img
                        src={imageUrl}
                        alt={`Existing ${index + 1}`}
                        loading="lazy"
                        style={{ 
                          height: '100%', 
                          objectFit: 'cover',
                          opacity: isDeleted ? 0.3 : 1,
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: isDeleted ? 'success.main' : 'error.main',
                          color: 'white',
                          '&:hover': { 
                            bgcolor: isDeleted ? 'success.dark' : 'error.dark' 
                          },
                        }}
                        onClick={() => isDeleted ? handleRestoreImage(imageUrl) : handleRemoveExistingImage(imageUrl)}
                      >
                        {isDeleted ? <CloudUpload /> : <Delete />}
                      </IconButton>
                    </ImageListItem>
                  );
                })}
              </ImageList>
            </Box>
          )}

          {/* New Images Upload */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={totalImageCount >= 5}
            >
              {t('marketplace.create.uploadImages')}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>

          {/* New Image Previews */}
          {imagePreview.length > 0 && (
            <ImageList sx={{ height: 200 }} cols={5} rowHeight={164}>
              {imagePreview.map((preview, index) => (
                <ImageListItem key={`new-${index}`} sx={{ position: 'relative' }}>
                  <img
                    src={preview}
                    alt={`New ${index + 1}`}
                    loading="lazy"
                    style={{ height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                    onClick={() => handleRemoveNewImage(index)}
                  >
                    <Delete />
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/marketplace/my-listings')}
              disabled={saving}
            >
              {t('marketplace.edit.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              sx={{ flex: 1 }}
            >
              {saving ? t('marketplace.edit.saving') : t('marketplace.edit.saveChanges')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditListingPage;